// Cadastrar coleção.
//
// É a mais delicada das quatro telas de cadastro, por uma razão só: o
// **`id_publico` é contrato**. Ele entra na URL que o analista de ArcGIS salva
// no projeto dele, e mudá-lo depois quebra o projeto — não há redirecionamento
// que resolva, porque o cliente guardou a string.
//
// Por isso a tela mostra a URL que vai nascer, antes de gravar. Ler
// `/collections/sigef-sp/items` e decidir é diferente de preencher um campo
// chamado "identificador público" e descobrir o efeito depois.
//
// A coleção nasce SEM versão vigente, sempre: apontar para uma versão é
// publicar, e publicar é operação separada, com gate de licença e período de
// graça (Q6). Criar já publicando pularia os dois.

import { useState, type ReactNode } from "react";
import type { Camada } from "../api.js";
import { Gaveta } from "../pecas/basicos.js";

/** Vira slug o que a pessoa digitou: acento fora, espaço vira hífen. */
function aSlug(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 61);
}

export function NovaColecao({
  camadas,
  aoFechar,
  aoSalvar,
  salvando,
}: {
  camadas: Camada[];
  aoFechar: () => void;
  aoSalvar: (corpo: Record<string, unknown>) => void;
  salvando: boolean;
}): ReactNode {
  const [titulo, setTitulo] = useState("");
  const [id, setId] = useState("");
  const [tocouId, setTocouId] = useState(false);
  // `null` enquanto ninguém escolheu; o efetivo cai na primeira da lista.
  // `useState(camadas[0]?.id)` fixaria o valor na PRIMEIRA renderização, quando
  // a lista ainda está vazia — e o botão ficaria travado para sempre. É o mesmo
  // erro que a gaveta de camada tinha.
  const [escolhida, setCamadaId] = useState<string | null>(null);
  const camadaId = escolhida ?? camadas[0]?.id ?? "";

  // Enquanto ninguém editar o identificador, ele acompanha o título. Depois de
  // editado, para de acompanhar: quem escolheu a URL não quer que ela mude ao
  // corrigir uma palavra do título.
  const idEfetivo = tocouId ? id : aSlug(titulo);
  const valido = /^[a-z][a-z0-9-]{1,60}$/.test(idEfetivo);
  const pronto = titulo.trim() !== "" && camadaId !== "" && valido;

  return (
    <Gaveta titulo="Nova coleção" subtitulo="o que o cliente vai consumir" aoFechar={aoFechar}>
      {camadas.length === 0 ? (
        <p className="nota-principio">
          Nenhuma camada cadastrada. Uma coleção publica uma camada — cadastre a camada primeiro.
        </p>
      ) : (
        <>
          <div className="form-secao">
            <div className="form-secao-titulo">Identificação</div>
            <div className="campo">
              <label htmlFor="titulo">Título</label>
              <input
                id="titulo"
                value={titulo}
                placeholder="Imóveis certificados — São Paulo"
                onChange={(e) => setTitulo(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="id-publico">Identificador público</label>
              <input
                id="id-publico"
                value={idEfetivo}
                onChange={(e) => {
                  setTocouId(true);
                  setId(e.target.value);
                }}
              />
              {idEfetivo !== "" && !valido ? (
                <div className="campo-ajuda campo-ajuda-erro">
                  Minúsculas, dígitos e <code className="mono">-</code>, começando por letra.
                </div>
              ) : (
                <div className="campo-ajuda">Acompanha o título até você editá-lo.</div>
              )}
            </div>

            <div className="aviso">
              <div>
                <b>Esta URL é contrato.</b>
                <p>
                  É o que o analista salva no projeto de ArcGIS dele. Mudar depois quebra o projeto
                  — não há redirecionamento que resolva, porque ele guardou a string.
                </p>
                <code className="mono url-contrato">
                  /ogc/collections/{valido ? idEfetivo : "…"}/items
                </code>
              </div>
            </div>
          </div>

          <div className="form-secao">
            <div className="form-secao-titulo">Origem</div>
            <div className="campo">
              <label htmlFor="camada">Camada</label>
              <select id="camada" value={camadaId} onChange={(e) => setCamadaId(e.target.value)}>
                {camadas.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                    {c.fonte_publicavel ? "" : " (fonte sem licença)"}
                  </option>
                ))}
              </select>
              <div className="campo-ajuda">
                A coleção nasce <b>sem versão vigente</b>: existe, e não serve nada ainda. Apontar
                para uma versão é publicar — operação separada, com gate de licença e graça.
              </div>
            </div>
          </div>

          <div className="acoes-form">
            <button
              className="btn btn-primario"
              disabled={!pronto || salvando}
              onClick={() =>
                aoSalvar({ id_publico: idEfetivo, camada_id: camadaId, titulo: titulo.trim() })
              }
            >
              {salvando ? "Criando…" : "Criar coleção"}
            </button>
            <button className="btn" onClick={aoFechar}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </Gaveta>
  );
}
